import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { FileText, Clock, ExternalLink, ChevronRight, Loader2, Ban, Send } from 'lucide-react';
import { fetchReports, fetchReport, excludeArticle, triggerReport } from '../api';
import { useAuth } from '../AuthContext';

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function ReportDetail({ id }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMaster = user?.role === 'master';
  const { data, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: () => fetchReport(id),
    enabled: !!id,
  });
  const [excluded, setExcluded] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');

  async function handleResend() {
    setSending(true); setSendMsg('');
    try {
      const dateStr = (data.created_at || '').slice(0, 10);
      await triggerReport('today', dateStr);
      setSendMsg('✅ 텔레그램 전송 완료');
    } catch { setSendMsg('❌ 전송 실패'); }
    setSending(false);
    setTimeout(() => setSendMsg(''), 4000);
  }

  const excludeMut = useMutation({
    mutationFn: ({ aid }) => excludeArticle(aid, '리포트에서 수동 배제'),
    onSuccess: (_, { aid }) => {
      setExcluded((prev) => new Set([...prev, aid]));
      qc.invalidateQueries({ queryKey: ['articles'] });
    },
  });

  if (isLoading) return <div className="empty-state"><div className="spinner" /></div>;
  if (!data) return null;

  const all = data.articles || [];
  // 배제된 기사를 제외하고 섹션별 수를 채움
  const active = all.filter(a => !excluded.has(a.id));
  function pickPersonnelFirst(pool, max) {
    const p = pool.filter(a => a.is_personnel === 1 || a.is_personnel === true);
    const o = pool.filter(a => !a.is_personnel);
    return [...p, ...o]
      .sort((a, b) => (b.is_personnel||0)-(a.is_personnel||0) || (b.relevance_score||0)-(a.relevance_score||0))
      .slice(0, max);
  }
  const domestic = pickPersonnelFirst(active.filter(a => a.lang === 'ko' && a.article_type === 'domestic'), 30);
  const column   = active.filter(a => a.article_type === 'column').sort((a,b) => (b.relevance_score||0)-(a.relevance_score||0)).slice(0, 10);
  const overseas = active.filter(a => a.lang === 'ko' && a.article_type === 'overseas').sort((a,b) => (b.relevance_score||0)-(a.relevance_score||0)).slice(0, 5);
  const foreign  = active.filter(a => a.lang === 'en' || a.article_type === 'foreign').sort((a,b) => (b.relevance_score||0)-(a.relevance_score||0)).slice(0, 3);

  const SECTIONS = [
    { key: 'domestic', label: '🇰🇷 국내 주요 뉴스', items: domestic, accent: '#38bdf8' },
    { key: 'column',   label: '📝 칼럼·분석',        items: column,   accent: '#a78bfa' },
    { key: 'overseas', label: '🌏 해외 소식 (국문)',  items: overseas, accent: '#34d399' },
    { key: 'foreign',  label: '🌐 영문 뉴스',         items: foreign,  accent: '#fbbf24' },
  ].filter(s => s.items.length > 0);

  function ArticleRow({ a, idx }) {
    return (
      <div style={{
        padding: '0.45rem 0', borderBottom: '1px solid #1e293b',
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        opacity: excluded.has(a.id) ? 0.3 : 1,
      }}>
        <span style={{ color: '#475569', fontSize: '0.78rem', flexShrink: 0, minWidth: '1.4rem', textAlign: 'right' }}>{idx}.</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <a href={a.link} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '0.85rem', color: '#e2e8f0', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', lineHeight: 1.4 }}>
            {(a.is_personnel === 1 || a.is_personnel === true) && <span style={{ color: '#fbbf24', fontSize: '0.7rem' }}>🔔</span>}
            {a.title}
            <ExternalLink size={10} opacity={0.4} />
          </a>
          <span style={{ fontSize: '0.68rem', color: '#475569', marginLeft: '0.4rem' }}>{a.source}</span>
        </div>
        {isMaster && (
          <button onClick={() => excludeMut.mutate({ aid: a.id })} disabled={excluded.has(a.id)}
            title="배제" style={{
              background: 'none', border: '1px solid #334155', borderRadius: '4px',
              color: excluded.has(a.id) ? '#334155' : '#ef4444',
              cursor: excluded.has(a.id) ? 'default' : 'pointer',
              padding: '2px 6px', fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0,
            }}>
            <Ban size={10} /> {excluded.has(a.id) ? '배제됨' : '배제'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="report-detail card">
      <div className="report-detail-header">
        <h2 style={{ fontSize: '1.1rem', color: '#f1f5f9' }}>{data.report_type}</h2>
        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatDate(data.created_at)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', color: '#475569' }}>전체 {all.length}건 수록 · 배제 {excluded.size}건</span>
        {isMaster && (
          <button onClick={handleResend} disabled={sending} style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: sending ? '#1e293b' : '#0c4a6e', border: '1px solid #0369a1',
            color: sending ? '#475569' : '#38bdf8', borderRadius: '6px',
            padding: '3px 10px', fontSize: '0.75rem', cursor: sending ? 'default' : 'pointer',
          }}>
            {sending ? <Loader2 size={12} className="spin" /> : <Send size={12} />}
            배제 반영해서 텔레그램 재발행
          </button>
        )}
        {sendMsg && <span style={{ fontSize: '0.75rem', color: sendMsg.startsWith('✅') ? '#4ade80' : '#f87171' }}>{sendMsg}</span>}
      </div>
      {SECTIONS.map(sec => (
        <div key={sec.key} style={{ marginTop: '1.25rem' }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: sec.accent, marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {sec.label} <span style={{ fontWeight: 400, color: '#475569', fontSize: '0.75rem' }}>TOP {sec.items.length}</span>
          </div>
          {sec.items.map((a, i) => <ArticleRow key={a.id} a={a} idx={i + 1} />)}
        </div>
      ))}
    </div>
  );
}

export default function ReportsPage() {
  const { id } = useParams();
  const [selectedId, setSelectedId] = useState(id ? parseInt(id) : null);

  const { data: reports, isLoading } = useQuery({
    queryKey: ['reports'],
    queryFn: fetchReports,
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <FileText size={20} color="#a78bfa" />
          리포트 기록
        </h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '320px 1fr' : '1fr', gap: '1rem' }}>
        <div className="card">
          {isLoading ? (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <Loader2 size={20} className="spin" />
            </div>
          ) : !reports || reports.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem', color: '#475569' }}>
              아직 생성된 리포트가 없습니다.
            </div>
          ) : (
            reports.map((r) => (
              <div
                key={r.id}
                className={`report-item ${selectedId === r.id ? 'active' : ''}`}
                onClick={() => setSelectedId(r.id)}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', color: '#e2e8f0', fontWeight: 500 }}>{r.report_type}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <Clock size={11} />
                    {formatDate(r.created_at)}
                  </div>
                </div>
                <ChevronRight size={14} color="#475569" />
              </div>
            ))
          )}
        </div>

        {selectedId && <ReportDetail id={selectedId} />}
      </div>

      <style>{`
        .report-item {
          display: flex; align-items: center; justify-content: space-between;
          padding: 0.75rem; border-radius: 7px; cursor: pointer;
          transition: background 0.15s; margin-bottom: 0.25rem;
        }
        .report-item:hover { background: #0f172a; }
        .report-item.active { background: #0c4a6e; }
        .report-detail-header {
          display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem;
        }
        .empty-state {
          display: flex; flex-direction: column; align-items: center;
          gap: 0.75rem; padding: 3rem 1rem; color: #475569;
        }
        .spin { animation: spin 0.7s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
